// The notification bell (docs/notification-bell-proposal.md). Pure helpers:
// the text of a row, where it leads, and its time. A notification carries a
// `kind`, the `params` its text needs and a `target` that names objects, never
// routes, because where a row leads depends on who reads it.

export const LEVEL_ICON = {
    info: "mdi-information",
    success: "mdi-check-circle",
    warning: "mdi-alert",
    error: "mdi-alert-circle",
}

export const LEVEL_COLOR = {
    info: "info",
    success: "success",
    warning: "accent",
    error: "error",
}

/** `{name}` placeholders filled from `params`; a missing one stays as written. */
export function fill(text, params = {}) {
    return String(text ?? "").replace(/\{(\w+)\}/g, (all, key) =>
        params[key] === undefined || params[key] === null ? all : String(params[key]))
}

/** The translated title of a notification, in the reader's language. */
export function notificationTitle(n, t) {
    const key = `notification_${n.kind}`
    const text = t(key)
    return fill(text && text !== key ? text : n.kind, n.params || {})
}

// A release notice's text fits the facts line up to about two lines of the
// 400 px menu; a longer one goes behind *Show details*.
export const FACTS_MAX = 90

/** The text of a release notice, "" when it only repeats the title. */
function releaseText(n, title) {
    const text = String(n.detail ?? "").trim()
    return text === String(title).trim() ? "" : text
}

/** True when the detail is written prose (a release notice), shown in the
 *  body font rather than the monospace box made for error lists. */
export function detailIsProse(n) {
    return n.kind === "produuzit_update"
}

/** The facts line under the title: what it is about, in a few words, minus
 *  what the title already says (*Project download ready: Bosrand*). */
export function notificationFacts(n, title = "") {
    // A release notice says what is new in its text: short, it reads under
    // the title; long, it is a detail to unfold.
    if (n.kind === "produuzit_update") {
        const text = releaseText(n, title)
        return text.length <= FACTS_MAX ? text : ""
    }
    const p = n.params || {}
    const parts = n.kind === "issue_new"
        ? [p.design, p.project, p.author]
        : [p.design, p.project]
    return parts.filter(v => v && !String(title).includes(String(v))).join(" · ")
}

/** The text behind *Show details*, or "" when there is none worth unfolding:
 *  a short release text is already the facts line, and a detail that only
 *  repeats the title says nothing. */
export function notificationDetail(n, title = "") {
    if (n.kind === "produuzit_update") {
        const text = releaseText(n, title)
        return text.length > FACTS_MAX ? text : ""
    }
    const detail = String(n.detail ?? "").trim()
    return detail === String(title).trim() ? "" : detail
}

const REPORT_LISTS = {
    project: { production: "production_files", management: "management_files" },
    company: { production: "company/production_files", management: "company/management_files" },
}

/**
 * Where a row leads: `{ route }` inside the app, `{ url }` outside it, or
 * null when there is nothing to open. `isProdWorker` users have no manager
 * screens, so their rows lead to the viewer or nowhere. `isManagementDoc`
 * tells the report lists apart (the reports store knows the document types).
 */
export function notificationTarget(n, { isProdWorker = false, isManagementDoc = () => false } = {}) {
    const tg = n.target || {}
    const p = tg.project
    switch (tg.type) {
        case "url":
            return tg.url ? { url: tg.url } : null
        case "export":
            return isProdWorker ? null : { route: `/downloads/project_exports/${tg.export}` }
        case "issue":
            if (isProdWorker) {
                // The viewer on the panel, with the hot issues dialog open on
                // the issue when it is hot. With no panel there is nothing a
                // production user can open, and the row is not clickable.
                const hot = n.params?.hot ? `?hot_issue=${tg.issue}` : ""
                if (tg.assembly) return { route: `/viewer/assembly/${tg.assembly}${hot}` }
                if (tg.module) return { route: `/viewer/module/${tg.module}${hot}` }
                return null
            }
            return p ? { route: `/projects/${p}/issues/${tg.issue}` } : { route: `/company/issues/${tg.issue}` }
        case "report": {
            if (isProdWorker) return null
            const bucket = isManagementDoc(tg.document_type) ? "management" : "production"
            return p
                ? { route: `/projects/${p}/${REPORT_LISTS.project[bucket]}/${tg.report}` }
                : { route: `/${REPORT_LISTS.company[bucket]}/${tg.report}` }
        }
        case "design":
            if (!p) return null
            if (isProdWorker) return { route: `/viewer/${tg.level || "assembly"}_design/${tg.design}` }
            return { route: `/projects/${p}/designs/${tg.level || "assembly"}/${tg.design}` }
        case "multipanel":
            if (isProdWorker) return { route: `/viewer/multipanel_group/${tg.group}` }
            return p ? { route: `/projects/${p}/multipanel/${tg.group}` } : null
        case "files":
            return p && !isProdWorker ? { route: `/projects/${p}/files` } : null
        case "project":
            return p && !isProdWorker ? { route: `/projects/${p}/overview` } : null
        default:
            return null
    }
}

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate()
}

/** `10:42` today, *Yesterday*, then `26 Oct`. */
export function notificationTime(iso, t, now = new Date()) {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ""
    if (sameDay(d, now)) {
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    }
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (sameDay(d, yesterday)) return t("yesterday")
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
}
